import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './Support.css';

const Support = ({ user, onClose }) => {
  const [formData, setFormData] = useState({
    subject: '',
    category: 'general',
    message: '',
    email: user?.email || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.message.trim()) {
      setSubmitStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Create support ticket in Firebase
      const ticketData = {
        ...formData,
        userId: user?.uid || null,
        userName: user?.displayName || 'Anonymous',
        userEmail: user?.email || formData.email,
        status: 'open',
        priority: formData.category === 'bug' ? 'high' : 'normal',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        responses: []
      };

      await addDoc(collection(db, 'support_tickets'), ticketData);

      setSubmitStatus({ 
        type: 'success', 
        message: 'Your support request has been submitted successfully! We\'ll get back to you within 24 hours.' 
      });

      // Clear form after successful submission
      setTimeout(() => {
        setFormData({
          subject: '',
          category: 'general',
          message: '',
          email: user?.email || ''
        });
        setSubmitStatus(null);
      }, 3000);

    } catch (error) {
      console.error('Error submitting support ticket:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'Failed to submit support request. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="support-overlay">
      <div className="support-container">
        <div className="support-header">
          <h2>Support Center</h2>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="support-content">
          <div className="support-info">
            <h3>How can we help you?</h3>
            <p>Fill out the form below and we'll get back to you as soon as possible.</p>
            
            <div className="contact-info">
              <div className="contact-item">
                <span className="contact-icon">📧</span>
                <div>
                  <strong>Email Support</strong>
                  <p>support@twizzler.app</p>
                </div>
              </div>
              <div className="contact-item">
                <span className="contact-icon">⏱️</span>
                <div>
                  <strong>Response Time</strong>
                  <p>Within 24 hours</p>
                </div>
              </div>
            </div>
          </div>

          <form className="support-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your@email.com"
                required
                disabled={!!user?.email}
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                <option value="general">General Inquiry</option>
                <option value="technical">Technical Support</option>
                <option value="bug">Bug Report</option>
                <option value="feature">Feature Request</option>
                <option value="billing">Billing / Pro Upgrade</option>
                <option value="account">Account Issues</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="Brief description of your issue"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Please describe your issue or question in detail..."
                rows="6"
                required
              />
            </div>

            {submitStatus && (
              <div className={`submit-status ${submitStatus.type}`}>
                <span className="status-icon">
                  {submitStatus.type === 'success' ? '✅' : '⚠️'}
                </span>
                {submitStatus.message}
              </div>
            )}

            <div className="form-actions">
              <button 
                type="button" 
                className="btn-cancel"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Support; 